import { Separator } from "@frontend/ui-kit/Separator";
import { useVirtualizer } from "@tanstack/react-virtual";
import cn from "classnames";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
    capitalize,
    EMOJI_GROUPS,
    EMOJI_PANEL_LIST_GAP,
    filterEmojiList,
    getActiveGroup,
    getEmojiGroupIndexList,
    getEmojiPanelLines,
    makeEmojiItemFromUserEmoji,
} from "../../lib";
import type { EmojiItem, UserEmoji } from "../../model";
import { EmojiPanelGroups } from "../EmojiPanelGroups";
import { EmojiPanelVirtualList } from "../EmojiPanelVirtualList";
import styles from "./EmojiPanel.module.scss";

export type EmojiPanelProps = {
    onEmojiClick: (emojiObject: EmojiItem) => void;
    popularEmojis?: UserEmoji[];
    size?: "s" | "m";
    searchQuery?: string;
    onSearchReset?: () => void;
};

export function EmojiPanel({
    onEmojiClick,
    popularEmojis = [],
    size = "s",
    searchQuery = "",
    onSearchReset,
}: EmojiPanelProps) {
    const [activeGroup, setActiveGroup] = useState(EMOJI_GROUPS[0].label);
    const groupToScrollTo = useRef<string | null>(null);

    const virtualListRef = useRef<HTMLDivElement>(null);
    const emojiList = useMemo(() => filterEmojiList(searchQuery), [searchQuery]);
    const rowList = useMemo(
        () =>
            getEmojiPanelLines({
                staticEmojiList: makeEmojiItemFromUserEmoji(popularEmojis),
                searchQuery,
                size,
            }),
        [popularEmojis, searchQuery, size],
    );
    const emojiGroupIndexMap = useMemo(() => getEmojiGroupIndexList(rowList), [rowList]);

    const virtualizer = useVirtualizer({
        count: rowList.length,
        getScrollElement: () => virtualListRef.current,
        estimateSize: (index) => rowList[index].height,
        gap: EMOJI_PANEL_LIST_GAP[size],
        paddingStart: 8,
    });

    const handleScrollToGroup = useCallback(
        (group: string) => virtualizer.scrollToIndex(emojiGroupIndexMap[group], { align: "start" }),
        [emojiGroupIndexMap, virtualizer],
    );

    const handleGroupButtonClick = useCallback(
        (group: string) => {
            if (searchQuery && onSearchReset) {
                onSearchReset();
                groupToScrollTo.current = group;
            } else {
                handleScrollToGroup(group);
            }
        },
        [handleScrollToGroup, searchQuery, onSearchReset],
    );

    useEffect(() => {
        setActiveGroup(getActiveGroup({ range: virtualizer.range, rowList: rowList }));
    }, [virtualizer.range, rowList]);

    // Обработка скролла к группе после сброса поиска
    useEffect(() => {
        if (groupToScrollTo.current && !searchQuery) {
            handleScrollToGroup(groupToScrollTo.current);
            groupToScrollTo.current = null;
        }
    }, [searchQuery, handleScrollToGroup]);

    const listClassname = cn(
        styles.emojiPanelList,
        styles[`emojiPanelListSize${capitalize(size)}`],
    );

    return (
        <div
            className={cn(styles.emojiPanel, styles[`emojiPanelSize${capitalize(size)}`])}
            data-testid="EmojiPanel"
        >
            <EmojiPanelGroups
                size={size}
                activeGroup={emojiList.length === 0 ? null : activeGroup}
                onClick={handleGroupButtonClick}
            />

            <Separator className={styles.separator} />

            <div className={listClassname}>
                {emojiList.length > 0 ? (
                    <EmojiPanelVirtualList
                        virtualizer={virtualizer}
                        size={size}
                        rowList={rowList}
                        virtualListRef={virtualListRef}
                        onEmojiClick={onEmojiClick}
                    />
                ) : (
                    <div className={styles.notFound}>Ничего не найдено</div>
                )}
            </div>
        </div>
    );
}
